/**
 * @module components/TechnicalReferents/TechnicalReferentsPanel
 * @description Panel de gestión de referentes técnicos del sistema.
 *
 * Muestra el listado de referentes técnicos con su carga de trabajo actual:
 * controles asignados, tareas pendientes y tasa de cumplimiento. Permite crear,
 * editar y desactivar referentes. La carga de trabajo se calcula en tiempo real
 * desde `mv_user_workload` (vista materializada de Supabase).
 *
 * Solo los administradores pueden crear y editar referentes. Los auditores
 * pueden ver la lista pero no modificarla.
 *
 * @see DataContext para las funciones createReferent(), updateReferent()
 * @see GlobalSidebar que incluye el acceso a este panel en el sidebar
 */
import React, { useState, useMemo } from 'react';
import {
  UserPlus,
  Edit,
  Trash2,
  Mail,
  Building,
  User,
  Check,
  X,
  AlertCircle,
  ArrowUpDown
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { TechnicalReferent } from '../../types/iso';

type SortOption = 'name-asc' | 'name-desc' | 'controls-desc' | 'controls-asc' | 'unit-asc' | 'status-active';

export function TechnicalReferentsPanel() {
  const { technicalReferents, addTechnicalReferent, updateTechnicalReferent, deleteTechnicalReferent } = useData();
  const { user } = useAuth();
  const [isAddingReferent, setIsAddingReferent] = useState(false);
  const [editingReferent, setEditingReferent] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    unit: '',
    isActive: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Only admin can manage referents
  if (user?.role !== 'admin') {
    return (
      <div className="p-4 text-center">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Solo los administradores pueden gestionar referentes técnicos
        </p>
      </div>
    );
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    } else {
      // Check for duplicate email
      const existingReferent = technicalReferents.find(r => 
        r.email === formData.email && r.id !== editingReferent
      );
      if (existingReferent) {
        newErrors.email = 'Este email ya está en uso';
      }
    }
    
    if (!formData.unit.trim()) {
      newErrors.unit = 'La unidad es requerida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (editingReferent) {
      updateTechnicalReferent(editingReferent, formData);
      setEditingReferent(null);
    } else {
      addTechnicalReferent({
        ...formData,
        role: 'technical_referent'
      });
      setIsAddingReferent(false);
    }
    
    setFormData({ name: '', email: '', unit: '', isActive: true });
    setErrors({});
  };

  const handleCancel = () => {
    setIsAddingReferent(false);
    setEditingReferent(null);
    setFormData({ name: '', email: '', unit: '', isActive: true });
    setErrors({});
  };

  const handleEdit = (referent: TechnicalReferent) => {
    setFormData({
      name: referent.name,
      email: referent.email,
      unit: referent.unit,
      isActive: referent.isActive
    });
    setEditingReferent(referent.id);
    setIsAddingReferent(false);
  };

  const handleDelete = (referentId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este referente técnico?')) {
      deleteTechnicalReferent(referentId);
    }
  };

  // Ordenar referentes según la opción seleccionada
  const sortedReferents = useMemo(() => {
    const sorted = [...technicalReferents];

    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'controls-desc':
        return sorted.sort((a, b) => b.assignedControls.length - a.assignedControls.length);
      case 'controls-asc':
        return sorted.sort((a, b) => a.assignedControls.length - b.assignedControls.length);
      case 'unit-asc':
        return sorted.sort((a, b) => a.unit.localeCompare(b.unit));
      case 'status-active':
        return sorted.sort((a, b) => {
          if (a.isActive === b.isActive) return a.name.localeCompare(b.name);
          return a.isActive ? -1 : 1;
        });
      default:
        return sorted;
    }
  }, [technicalReferents, sortBy]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-900 dark:text-white">
          Referentes Técnicos
        </h3>
        {!isAddingReferent && !editingReferent && (
          <button
            onClick={() => setIsAddingReferent(true)}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="Agregar referente"
          >
            <UserPlus className="w-3 h-3" />
            <span>Agregar</span>
          </button>
        )}
      </div>

      {/* Sort Options */}
      {!isAddingReferent && !editingReferent && technicalReferents.length > 0 && (
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="name-asc">Nombre (A-Z)</option>
            <option value="name-desc">Nombre (Z-A)</option>
            <option value="controls-desc">Más controles asignados</option>
            <option value="controls-asc">Menos controles asignados</option>
            <option value="unit-asc">Unidad (A-Z)</option>
            <option value="status-active">Activos primero</option>
          </select>
        </div>
      )}

      {/* Add/Edit Form */}
      {(isAddingReferent || editingReferent) && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            {editingReferent ? 'Editar Referente' : 'Nuevo Referente Técnico'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre completo *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full text-sm border rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ej: Juan Pérez"
                autoFocus
              />
              {errors.name && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email corporativo *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full text-sm border rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="juan.perez@company.com"
              />
              {errors.email && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Unidad/Departamento *
              </label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className={`w-full text-sm border rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.unit 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ej: Seguridad de la Información"
              />
              {errors.unit && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.unit}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="isActive" className="text-xs text-gray-700 dark:text-gray-300">
                Referente activo (puede recibir asignaciones)
              </label>
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <Check className="w-4 h-4" />
                <span>{editingReferent ? 'Actualizar Referente' : 'Crear Referente'}</span>
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Cancelar</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Referents List */}
      <div className="space-y-2">
        {sortedReferents.length === 0 ? (
          <div className="text-center py-8">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No hay referentes técnicos registrados
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Los referentes recibirán notificaciones por email cuando se les asignen controles
            </p>
          </div>
        ) : (
          sortedReferents.map((referent) => (
            <div
              key={referent.id}
              className={`p-3 border rounded-lg transition-colors ${
                referent.isActive
                  ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    {referent.avatar ? (
                      <img
                        src={referent.avatar}
                        alt={referent.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${referent.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}`}>
                        <User className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {referent.name}
                    </h4>
                    {referent.role === 'admin' && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full font-medium">
                        Administrador
                      </span>
                    )}
                    {!referent.isActive && (
                      <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full">
                        Inactivo
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                      <Mail className="w-3 h-3 mr-1" />
                      <span className="truncate">{referent.email}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                      <Building className="w-3 h-3 mr-1" />
                      <span className="truncate">{referent.unit}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {referent.assignedControls.length} control{referent.assignedControls.length !== 1 ? 'es' : ''} asignado{referent.assignedControls.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>

                {!isAddingReferent && !editingReferent && (
                  <div className="flex items-center space-x-1 ml-2">
                    {referent.role !== 'admin' && (
                      <button
                        onClick={() => handleEdit(referent)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Editar referente"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                    )}
                    {referent.role === 'admin' ? (
                      <span
                        className="p-1.5 text-gray-300 dark:text-gray-600 cursor-not-allowed rounded"
                        title="Gestionar desde Gestión de Usuarios"
                      >
                        <Trash2 className="w-3 h-3" />
                      </span>
                    ) : (
                      <button
                        onClick={() => handleDelete(referent.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Eliminar referente"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span>
            {technicalReferents.filter(r => r.isActive).length} referente{technicalReferents.filter(r => r.isActive).length !== 1 ? 's' : ''} activo{technicalReferents.filter(r => r.isActive).length !== 1 ? 's' : ''}
          </span>
          <span>
            Total: {technicalReferents.length}
          </span>
        </div>
      </div>
    </div>
  );
}